import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import OfferCard from "../components/OfferCard";
import { Button } from "../components/ui/button";
import CategoryPicker from "../components/CategoryPicker";
import { Tag, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Offers() {
  const { user } = useAuth();
  const [data, setData] = useState({ offers: [], live_count: 0, cap: 100 });
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/offers", { params: category !== "all" ? { category } : {} })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(load, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm("Remove this offer? It will disappear from the site immediately.")) return;
    try {
      await api.delete(`/admin/offers/${id}`);
      toast.success("Offer removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="offers-page">
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><Tag className="w-3 h-3" /> Live for 24 hours only</p>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter">
          Today&apos;s <span className="bg-[#FFCC00] px-2 -mx-1">offers.</span>
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-sm font-mono text-zinc-500" data-testid="offers-live-count">{data.live_count} / {data.cap} live</span>
          <div className="w-60">
            <CategoryPicker value={category} onChange={setCategory} categories={cats} allLabel="All services" testid="offers-category" />
          </div>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading offers...</div>
      ) : data.offers.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center" data-testid="offers-empty">
          <Tag className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="font-display text-xl font-bold">No live offers right now</p>
          <p className="text-sm text-zinc-600 mt-1">Check back soon — new offers go live every day.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="offers-grid">
          {data.offers.map((o) => (
            <OfferCard key={o.id} offer={o} action={user?.role === "admin" && (
              <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => remove(o.id)} data-testid={`offer-remove-${o.id}`}>Remove</Button>
            )} />
          ))}
        </div>
      )}
    </div>
  );
}
